import React, { useEffect, useRef, useState } from "react";
import { X, Save, Upload, Music2, Plus, Trash2, ExternalLink, BadgeCheck, Crown, Star } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { CATEGORIES as CATS } from "../lib/categories";

const CATEGORIES = [
  { value: "", label: "— Non définie —" },
  ...CATS.map((c) => ({ value: c.key, label: `${c.emoji} ${c.label}` })),
];

const inputCls = "w-full rounded-lg bg-white/[0.03] border border-white/10 px-3 py-2 text-sm focus:outline-none focus:border-brand-orange/60 transition-colors";

/**
 * AdminUserEditModal
 * Permet à l'admin de modifier complètement un utilisateur :
 * - profil (nom, bio, email, catégorie, pays, ville, photo, cover)
 * - badges (admin, artist, verified, premium, featured)
 * - tracks existants (éditer/supprimer) + ajout d'une vidéo externe
 */
export const AdminUserEditModal = ({ user, onClose, onUpdated }) => {
  const [form, setForm] = useState(() => ({
    name: user.name || "",
    email: user.email || "",
    bio: user.bio || "",
    category: user.category || "",
    country: user.country || "",
    city: user.city || "",
    picture: user.picture || "",
    cover_path: user.cover_path || "",
    is_admin: !!user.is_admin,
    is_artist: !!user.is_artist,
    is_verified: !!user.is_verified,
    is_premium: !!user.is_premium,
    is_featured: !!user.is_featured,
  }));
  const [saving, setSaving] = useState(false);
  const [tracks, setTracks] = useState([]);
  const [tracksLoading, setTracksLoading] = useState(true);
  const [uploadingPic, setUploadingPic] = useState(false);
  const picRef = useRef(null);

  // New external video
  const [newVideo, setNewVideo] = useState({ external_url: "", title: "", category: "music", genre: "" });
  const [addingVideo, setAddingVideo] = useState(false);

  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, []);

  useEffect(() => {
    setTracksLoading(true);
    api.get(`/admin/users/${user.user_id}/tracks`)
      .then(({ data }) => setTracks(data))
      .catch(() => setTracks([]))
      .finally(() => setTracksLoading(false));
  }, [user.user_id]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.patch(`/admin/users/${user.user_id}`, form);
      onUpdated?.(data);
      toast.success("Profil mis à jour");
    } catch (e) {
      toast.error(e?.response?.data?.error || "Erreur lors de la mise à jour");
    } finally {
      setSaving(false);
    }
  };

  const uploadPicture = async (file) => {
    const fd = new FormData();
    fd.append("file", file);
    fd.append("kind", "profile");
    setUploadingPic(true);
    try {
      const { data } = await api.post("/upload/image", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setForm((f) => ({ ...f, picture: data.url }));
      toast.success("Photo uploadée — n'oublie pas d'enregistrer");
    } catch {
      toast.error("Upload échoué");
    } finally {
      setUploadingPic(false);
    }
  };

  const deleteTrack = async (t) => {
    if (!window.confirm(`Supprimer "${t.title}" ?`)) return;
    try {
      await api.delete(`/admin/tracks/${t.track_id}`);
      setTracks((prev) => prev.filter((x) => x.track_id !== t.track_id));
      toast.success("Supprimé");
    } catch {
      toast.error("Erreur");
    }
  };

  const toggleFeatured = async (t) => {
    try {
      const { data } = await api.patch(`/admin/tracks/${t.track_id}`, { is_featured: !t.is_featured });
      setTracks((prev) => prev.map((x) => (x.track_id === t.track_id ? data : x)));
    } catch {
      toast.error("Erreur");
    }
  };

  const editTrackTitle = async (t) => {
    const title = window.prompt(`Nouveau titre pour "${t.title}" ?`, t.title);
    if (!title || title === t.title) return;
    try {
      const { data } = await api.patch(`/admin/tracks/${t.track_id}`, { title });
      setTracks((prev) => prev.map((x) => (x.track_id === t.track_id ? data : x)));
      toast.success("Titre mis à jour");
    } catch {
      toast.error("Erreur");
    }
  };

  const addVideo = async (e) => {
    e.preventDefault();
    if (!newVideo.external_url || !newVideo.title) {
      toast.error("URL et titre requis");
      return;
    }
    setAddingVideo(true);
    try {
      const { data } = await api.post(`/admin/users/${user.user_id}/tracks`, newVideo);
      setTracks((prev) => [data, ...prev]);
      setNewVideo({ external_url: "", title: "", category: newVideo.category, genre: "" });
      toast.success("Vidéo ajoutée ✨");
    } catch (e) {
      toast.error(e?.response?.data?.error || "Lien non reconnu (YouTube/TikTok/Vimeo)");
    } finally {
      setAddingVideo(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[1000] bg-black/75 backdrop-blur-sm flex items-start sm:items-center justify-center p-0 sm:p-6 overflow-y-auto"
      onClick={onClose}
      data-testid="admin-user-edit-modal"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full sm:max-w-4xl sm:rounded-2xl border border-white/10 bg-[#0d0706] shadow-2xl my-4 flex flex-col max-h-[95vh]"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-white/10 sticky top-0 bg-[#0d0706] z-10">
          <div className="flex items-center gap-3 min-w-0">
            {form.picture ? (
              <img src={form.picture} alt="" className="w-10 h-10 rounded-full object-cover" />
            ) : (
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-brand-red to-brand-orange flex items-center justify-center font-black">
                {(form.name || "?").charAt(0).toUpperCase()}
              </div>
            )}
            <div className="min-w-0">
              <p className="text-[10px] uppercase tracking-widest text-brand-orange font-bold">Édition profil</p>
              <h3 className="font-display text-xl font-black truncate">{user.name}</h3>
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <button
              onClick={save}
              disabled={saving}
              className="btn-primary inline-flex items-center gap-1.5 text-sm disabled:opacity-60"
              data-testid="admin-user-save-btn"
            >
              <Save size={14} /> {saving ? "…" : "Enregistrer"}
            </button>
            <button onClick={onClose} className="p-2 rounded-full hover:bg-white/10" aria-label="Fermer" data-testid="admin-user-close">
              <X size={18} />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="overflow-y-auto p-6 space-y-8">
          {/* Section: Identité */}
          <section>
            <h4 className="text-xs uppercase tracking-widest text-white/50 font-bold mb-3">Identité</h4>
            <div className="grid sm:grid-cols-[120px_1fr] gap-4">
              <div className="flex flex-col items-center gap-2">
                {form.picture ? (
                  <img src={form.picture} alt="" className="w-28 h-28 rounded-xl object-cover border border-white/10" />
                ) : (
                  <div className="w-28 h-28 rounded-xl bg-gradient-to-br from-brand-red to-brand-orange flex items-center justify-center font-black text-3xl">
                    {(form.name || "?").charAt(0).toUpperCase()}
                  </div>
                )}
                <input
                  ref={picRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => e.target.files?.[0] && uploadPicture(e.target.files[0])}
                  data-testid="admin-user-pic-input"
                />
                <button
                  type="button"
                  onClick={() => picRef.current?.click()}
                  disabled={uploadingPic}
                  className="text-xs btn-ghost inline-flex items-center gap-1 px-3 py-1.5"
                  data-testid="admin-user-pic-upload"
                >
                  <Upload size={11} /> {uploadingPic ? "…" : "Photo"}
                </button>
              </div>

              <div className="grid sm:grid-cols-2 gap-3">
                <Field label="Nom *">
                  <input value={form.name} onChange={set("name")} className={inputCls} data-testid="admin-user-name" />
                </Field>
                <Field label="Email">
                  <input value={form.email} onChange={set("email")} className={inputCls} data-testid="admin-user-email" />
                </Field>
                <Field label="Catégorie">
                  <select value={form.category} onChange={set("category")} className={inputCls} data-testid="admin-user-category">
                    {CATEGORIES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
                  </select>
                </Field>
                <Field label="Pays">
                  <input value={form.country} onChange={set("country")} className={inputCls} data-testid="admin-user-country" />
                </Field>
                <Field label="Ville">
                  <input value={form.city} onChange={set("city")} className={inputCls} data-testid="admin-user-city" />
                </Field>
                <Field label="URL photo (alternative)">
                  <input value={form.picture} onChange={set("picture")} placeholder="https://…" className={inputCls} data-testid="admin-user-picture-url" />
                </Field>
                <Field label="Bio" full>
                  <textarea value={form.bio} onChange={set("bio")} rows={3} className={inputCls} data-testid="admin-user-bio" />
                </Field>
              </div>
            </div>
          </section>

          {/* Section: Badges */}
          <section>
            <h4 className="text-xs uppercase tracking-widest text-white/50 font-bold mb-3">Badges & rôles</h4>
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
              <BadgeToggle label="Artiste" icon={Music2} active={form.is_artist} onToggle={() => setForm((f) => ({ ...f, is_artist: !f.is_artist }))} testid="badge-artist" />
              <BadgeToggle label="Vérifié" icon={BadgeCheck} active={form.is_verified} onToggle={() => setForm((f) => ({ ...f, is_verified: !f.is_verified }))} color="text-brand-yellow" testid="badge-verified" />
              <BadgeToggle label="Premium" icon={Crown} active={form.is_premium} onToggle={() => setForm((f) => ({ ...f, is_premium: !f.is_premium }))} color="text-brand-orange" testid="badge-premium" />
              <BadgeToggle label="Vedette" icon={Star} active={form.is_featured} onToggle={() => setForm((f) => ({ ...f, is_featured: !f.is_featured }))} color="text-brand-red" testid="badge-featured" />
              <BadgeToggle label="Admin" icon={Crown} active={form.is_admin} onToggle={() => setForm((f) => ({ ...f, is_admin: !f.is_admin }))} color="text-brand-red" danger testid="badge-admin" />
            </div>
          </section>

          {/* Section: Contenus / Tracks */}
          <section>
            <div className="flex items-center justify-between mb-3">
              <h4 className="text-xs uppercase tracking-widest text-white/50 font-bold">Contenus ({tracks.length})</h4>
            </div>

            {/* Ajout rapide vidéo */}
            <form onSubmit={addVideo} className="rounded-xl border border-dashed border-brand-orange/30 bg-brand-orange/5 p-3 mb-3" data-testid="admin-add-video-form">
              <p className="text-xs font-bold text-brand-orange mb-2 inline-flex items-center gap-1">
                <Plus size={12} /> Ajouter une vidéo (YouTube, TikTok, Vimeo…)
              </p>
              <div className="grid sm:grid-cols-[1fr_1fr_180px_140px_auto] gap-2">
                <input
                  value={newVideo.external_url}
                  onChange={(e) => setNewVideo({ ...newVideo, external_url: e.target.value })}
                  placeholder="https://youtube.com/watch?v=… *"
                  className={inputCls}
                  data-testid="admin-new-video-url"
                />
                <input
                  value={newVideo.title}
                  onChange={(e) => setNewVideo({ ...newVideo, title: e.target.value })}
                  placeholder="Titre *"
                  className={inputCls}
                  data-testid="admin-new-video-title"
                />
                <select
                  value={newVideo.category}
                  onChange={(e) => setNewVideo({ ...newVideo, category: e.target.value })}
                  className={inputCls}
                  data-testid="admin-new-video-category"
                >
                  {CATEGORIES.filter((c) => c.value).map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
                </select>
                <input
                  value={newVideo.genre}
                  onChange={(e) => setNewVideo({ ...newVideo, genre: e.target.value })}
                  placeholder="Genre"
                  className={inputCls}
                  data-testid="admin-new-video-genre"
                />
                <button
                  type="submit"
                  disabled={addingVideo}
                  className="btn-primary text-sm inline-flex items-center gap-1 px-4 disabled:opacity-60"
                  data-testid="admin-new-video-submit"
                >
                  <Plus size={14} /> {addingVideo ? "…" : "Ajouter"}
                </button>
              </div>
            </form>

            {tracksLoading ? (
              <div className="text-sm text-white/50 py-6 text-center">Chargement…</div>
            ) : tracks.length === 0 ? (
              <div className="text-sm text-white/50 py-6 text-center border border-white/5 rounded-xl">
                Aucun contenu. Ajoute une vidéo ci-dessus ✨
              </div>
            ) : (
              <ul className="space-y-1.5" data-testid="admin-user-tracks-list">
                {tracks.map((t) => (
                  <li
                    key={t.track_id}
                    className="flex items-center gap-2 p-2.5 rounded-lg bg-white/[0.02] border border-white/5 hover:border-brand-orange/30"
                    data-testid={`admin-track-row-${t.track_id}`}
                  >
                    <div className="w-9 h-9 rounded-md bg-white/5 flex items-center justify-center shrink-0 text-white/50">
                      <Music2 size={14} />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="font-semibold text-sm truncate">{t.title}</p>
                      <p className="text-[11px] text-white/50 truncate">
                        {t.category}{t.genre ? ` · ${t.genre}` : ""} · {t.plays || 0} lectures
                      </p>
                    </div>
                    {t.external_url && (
                      <a
                        href={t.external_url}
                        target="_blank"
                        rel="noreferrer"
                        className="text-white/40 hover:text-brand-orange p-1.5"
                        aria-label="Ouvrir"
                      >
                        <ExternalLink size={13} />
                      </a>
                    )}
                    <button
                      onClick={() => toggleFeatured(t)}
                      className={`text-xs px-2 py-1 rounded-full ${t.is_featured ? "bg-brand-yellow/20 text-brand-yellow" : "text-white/40 hover:text-brand-yellow"}`}
                      title="Toggle featured"
                      data-testid={`admin-track-feature-${t.track_id}`}
                    >
                      <Star size={12} />
                    </button>
                    <button
                      onClick={() => editTrackTitle(t)}
                      className="text-[10px] uppercase tracking-wider text-white/50 hover:text-brand-orange px-2"
                      data-testid={`admin-track-edit-${t.track_id}`}
                    >
                      Éditer
                    </button>
                    <button
                      onClick={() => deleteTrack(t)}
                      className="text-red-400/70 hover:text-red-400 p-1.5"
                      aria-label="Supprimer"
                      data-testid={`admin-track-delete-${t.track_id}`}
                    >
                      <Trash2 size={13} />
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </section>
        </div>
      </div>
    </div>
  );
};

const Field = ({ label, children, full }) => (
  <div className={full ? "sm:col-span-2" : ""}>
    <label className="block text-[10px] uppercase tracking-widest text-white/50 mb-1 font-bold">{label}</label>
    {children}
  </div>
);

const BadgeToggle = ({ label, icon: Icon, active, onToggle, color = "text-white", danger = false, testid }) => (
  <button
    type="button"
    onClick={onToggle}
    className={`flex flex-col items-center gap-1 p-2.5 rounded-xl border transition-all ${
      active
        ? danger
          ? "border-red-500/40 bg-red-500/10"
          : "border-brand-orange/40 bg-brand-orange/10"
        : "border-white/10 bg-white/[0.02] hover:border-white/25"
    }`}
    data-testid={testid}
  >
    <Icon size={16} className={active ? color : "text-white/40"} />
    <span className={`text-[10px] font-bold uppercase tracking-wider ${active ? "text-white" : "text-white/50"}`}>
      {label}
    </span>
    <span className={`text-[9px] ${active ? "text-brand-orange" : "text-white/30"}`}>
      {active ? "ACTIF" : "off"}
    </span>
  </button>
);

export default AdminUserEditModal;
